'use client'

import { ChevronBottom, Translate } from '@quark/icons'
import { Locale } from '@enums/strapiTypesAndOperators'
import { getClassNames } from '@helpers/productWidgetHelper'
import { getLanguagePickerOptions } from '@utils/localisation/languagePickerOptions'
import { useEffect, useRef, useState } from 'react'
import { LOCALE_TOOLTIP_TARGET_ID } from '@constants/localisationConstants'
import LocaleHeaderTooltip from './LocaleHeaderTooltip'
import styles from './productNav.module.scss'

const LanguagePicker = ({
  selectedLanguage,
  availableLanguages,
  darkIcon = false,
  tooltipLang,
}: {
  selectedLanguage: Locale
  availableLanguages: Locale[]
  darkIcon?: boolean
  tooltipLang?: string
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const close = () => setIsOpen(false)
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) close()
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    window.addEventListener('scroll', close, true)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
      window.removeEventListener('scroll', close, true)
    }
  }, [isOpen])

  if (availableLanguages.length <= 1) return null
  const languagePickerOptions = getLanguagePickerOptions(selectedLanguage, availableLanguages)
  return (
    <div className={styles.languagePickerContainer} ref={containerRef}>
      <div
        id={LOCALE_TOOLTIP_TARGET_ID}
        className={getClassNames([styles.languagePicker], {
          [styles.languagePickerLight]: !darkIcon,
          [styles.languagePickerDark]: darkIcon,
        })}
        onClick={() => setIsOpen(prev => !prev)}>
        <Translate />
        <div className={getClassNames([styles.chevron], { [styles.invert]: isOpen })}>
          <ChevronBottom />
        </div>
      </div>
      <div className={getClassNames([styles.languageDropDownContainer], { [styles.languageDropDownOpen]: isOpen })}>
        <div className={styles.languageDropDown}>
          {languagePickerOptions.options?.map((lang, i) => (
            <div
              key={i}
              className={getClassNames([styles.languageOption], {
                [styles.selectedLanguage]: !!lang.selected,
              })}
              onClick={lang.props.onClick}>
              {lang.icon}
              <a href={lang.props.href} rel={lang.props.rel} onClick={e => e.preventDefault()}>
                {lang.name}
              </a>
            </div>
          ))}
        </div>
      </div>
      {tooltipLang && tooltipLang === selectedLanguage && <LocaleHeaderTooltip lang={tooltipLang} />}
    </div>
  )
}

export default LanguagePicker
